import { useCallback } from 'react'
import { useQueryClient } from 'react-query'

import type { CustomField } from '@sales-center/common'
import { toast } from '@sales-center/components'

import { customValuesToInputModel } from '@user/components/CustomFields'
import {
  MISSING_AUTO_CLOSE_REQUIRED_FIELDS_QUERY_KEY,
  SCHEDULE_LEAD_IS_CLOSABLE_QUERY_KEY,
} from '@user/query/useMissingAutoCloseRequiredFields'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { updateMissingAutoCloseRequiredFields } from '@user/store/lead'
import { isAPIError } from '@user/store/services/crud'

import type { RequiredFieldsFormModel } from '../typings'

type Props = {
  leadId: number
  missingFields: CustomField[]
  onClose: () => void
}

export function useHandlers(props: Props) {
  const { leadId, missingFields, onClose } = props

  const dispatch = useAppDispatch()
  const queryClient = useQueryClient()

  const onSubmit = useCallback(
    async (model: RequiredFieldsFormModel) => {
      const values = customValuesToInputModel(model, missingFields)

      const response = await dispatch(
        updateMissingAutoCloseRequiredFields({ leadId, values }),
      )

      if (updateMissingAutoCloseRequiredFields.rejected.match(response)) {
        toast.error(
          isAPIError(response.payload)
            ? response.payload.Message
            : 'Failed to update required fields.',
        )
        return
      }

      await Promise.all([
        queryClient.invalidateQueries([
          MISSING_AUTO_CLOSE_REQUIRED_FIELDS_QUERY_KEY,
          leadId,
        ]),
        queryClient.invalidateQueries([
          SCHEDULE_LEAD_IS_CLOSABLE_QUERY_KEY,
          leadId,
        ]),
      ])

      toast.success('Required fields updated.')
      onClose()
    },
    [dispatch, leadId, missingFields, onClose, queryClient],
  )

  return { onSubmit }
}
